<template>
  <div>
    <article>
      <header>
        <h2>{{$t('upcoming.title')}}</h2>
      </header>
      <main>
        <table cellspacing="0">
          <tr v-for="(entry, index) in $i18n.messages.de.upcoming.dates" :key="index" :class="{upcoming: !entry.date}">
            <td style="font-variant: tabular-nums; white-space: nowrap;">
              <p>
                <i18n tag="strong" path="dateFormat" v-if="entry.date">
                  <template #day>{{ entry.date.day }}</template>
                  <template #monthName>{{ $t(`monthNames[${ entry.date.month - 1}]`) }}</template>
                  <template #year>{{ entry.date.year }}</template>
                </i18n>
                <strong v-else>
                  TBD
                </strong>
              </p>
            </td>
            <td>
              <p>
                <strong>{{$t(`upcoming.dates[${index}].title`)}}</strong><br>
                {{$t(`upcoming.dates[${index}].description`)}}
              </p>
            </td>
          </tr>
        </table>
      </main>
    </article>
    <article class="calendar-wrapper">
      <header>
        <h1>{{$t(`monthNames[${eventDates[0].getMonth()}]`)}} {{eventDates[0].getFullYear()}}</h1>
      </header>
      <main class="calendar">
        <div class="header" v-for="day in 7" :key="day">
          {{$t(`weekdaysShort[${day-1}]`)}}
        </div>
        <template v-for="week in 6">
          <div class="day"
               v-for="day in 7" :key="`${week}-${day}`"
               :class="[
                {
                  inMonth: dayInMonth(0, week, day)
                },
                ...dayClasses(week, day),
               ]"
               :style="{
                 'animation-delay': `${(day + week * 7) * 1}s`,
               }">
            <div>
              {{displayDay(0, week, day)}}
            </div>
            <div style="font-weight: bold"
                 v-for="(eventDate, index) in eventDates"
                 v-if="displayDay(index, week, day) === eventDate.getDate()"
            >
              {{$t(`party_dates[${index}]`)}}
            </div>
          </div>
        </template>
      </main>
    </article>
    <div class="timetable">
      <PartyAgenda
        v-for="party in PARTIES"
        :accentClass="party.accentClass"
        :title="party.title"
        :date="party.date"
        :location="party.location"
        :events="party.events"
        :remarks="party.remarks"
      />
    </div>
    <div class="save-the-date" v-if="nextPartyDate">
      <article>
        <header>
          <h3>Save the Date</h3>
        </header>
        <main>
          <i18n tag="p" :path="ticketSaleStartAvailable ? 'saveTheDate' : 'saveTheDateWithoutSale'">
            <template v-slot:nextPartyDay>{{ nextPartyDate.day }}</template>
            <template v-slot:nextPartyMonth>{{ $t(`monthNames[${nextPartyDate.month - 1}]`) }}</template>
            <template v-slot:nextPartyYear>{{ nextPartyDate.year }}</template>
            <template v-slot:ticketSaleStartDay>{{ ticketSaleStart.day }}</template>
            <template v-slot:ticketSaleStartMonth>{{ $t(`monthNames[${ticketSaleStart.month - 1}]`) }}</template>
            <template v-slot:ticketSaleStartYear>{{ ticketSaleStart.year }}</template>
            <template v-slot:ticketSaleStartHour>{{ ticketSaleStart.hour }}</template>
          </i18n>
        </main>
      </article>
    </div>
  </div>
</template>


<script>
  import PARTIES from '@/data/parties.yml';
  import PartyAgenda from '@/components/PartyAgenda';

  export default {
    components: { PartyAgenda },
    data() {
      return {
        nextPartyDate: JSON.parse(process.env.VUE_APP_NEXT_PARTY_DATE),
        ticketSaleStart: JSON.parse(process.env.VUE_APP_TICKET_SALE_START),
        ticketSaleStartAvailable: process.env.VUE_APP_NEXT_PARTY_DATE != process.env.VUE_APP_TICKET_SALE_START,
        eventDates: [
          new Date(2024, 10 - 1, 4),
          new Date(2024, 10 - 1, 5),
          new Date(2024, 10 - 1, 6),
        ],
        eventClasses: [
          "accent-panda accent-didi",
          "accent-volth",
          "accent-patricia",
        ],
        PARTIES,
      };
    },
    computed: {
      firstOfMonth() {
        return this.eventDates.map(eventDate => {
          let newDate = new Date(eventDate.getTime());
          newDate.setDate(1);
          return newDate;
        })
      },
      lastOfMonth() {
        return this.firstOfMonth.map(date => {
          let newDate = new Date(date.getTime());
          newDate.setMonth(newDate.getMonth() + 1);
          newDate.setDate(0);
          return newDate;
        })
      },
    },
    methods: {
      dayHasEvent(week, day) {
        return this.eventDates.some((date, idx) => {
          return this.displayDay(idx, week, day) === date.getDate()
        });
      },
      dayClasses(week, day) {
        return this.eventDates.map((date, idx) => {
          if (this.displayDay(idx, week, day) === date.getDate()) {
            return ["party", this.eventClasses[idx]]
          }
        });
      },
      dayInMonth(idx, week, day) {
        return this.firstOfMonth[idx].getDate() <= this.displayDay(idx, week, day)
          && this.lastOfMonth[idx].getDate() >= this.displayDay(idx, week, day);
      },
      displayDay(idx, week, day) {
        if (week === 1 && day < this.firstOfMonth[idx].getDay()) {
          return null;
        }

        let dayOffset = (week - 1) * 7 + day - this.firstOfMonth[idx].getDay() + 1;
        if (dayOffset > this.lastOfMonth[idx].getDate()) {
          return null;
        }

        return dayOffset;
      },
      weekDayLocalized(day) {
        return ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][day - 1];
      },
    },
  };
</script>


<style scoped lang="scss">
  $colorMain: #1976d2;
  $colorAccent: #fdd835;
  $colorPatricia: #F06292;

  $accentColors: (
    "didi": $colorMain,
    "patricia": $colorPatricia,
    "volth": $colorAccent,
    "panda": #00963D,
  );

  @function contrast-color($color, $dark: #000, $light: #fff, $threshold: 55%) {
    @return if(lightness($color) < $threshold, $light, $dark)
  }

  article.calendar-wrapper {
    background: $colorAccent;

    @media screen and (max-width: 1000px) {
      display: none;
    }

    > header {
      text-align: center;
    }

    > main {
      padding: 8px;
    }

    > main.calendar {
      // background: white;
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      grid-gap: 12px;

      .day {
        aspect-ratio: 6/4;
        padding: 6px;
        background-color: rgba(white, 0.5);
        border-radius: 6px;
        opacity: 0.3;
        color: black;
        flex-shrink: 0;
        overflow: hidden;
        font-size: 80%;

        &.inMonth {
          opacity: 1;
        }

        @each $name, $color in $accentColors {
          &.accent-#{$name} {
            background: $color;
            color: contrast-color($color);
          }

          @each $name2, $color2 in $accentColors {
            @if $name != $name2 {
              &.accent-#{$name}.accent-#{$name2} {
                background: linear-gradient(-30deg, $color2 20%, $color 80%);
              }
            }
          }
        }
      }

      .party {
        position: relative;
        background: white;
        box-shadow: 0 2px 12px rgba(black, 0.4);

        > * {
          position: relative;
          z-index: 2;
          color: black;
        }

        // &:before {
        //   $scale: 20%;
        //   content: '';
        //   position: absolute;
        //   top: -$scale;
        //   left: -$scale;
        //   right: -$scale;
        //   bottom: -$scale;
        //   background: rgba(white, 0.6);
        //   border-radius: 100%;
        //   transform: rotate(-10deg);
        //   z-index: 1;
        // }
      }

      .header {
        background-color: transparent;
        margin-left: 8px;
        font-weight: bold;
      }

      @media screen and (max-width: 720px) {
        display: none;
      }
    }
  }

  .timetable {
    float: left;
    margin-top: -600px;
    margin-left: -40px;
    z-index: 100;
    color: white;
    position: relative;
    display: block;
    border-radius: 8px;
    margin-bottom: 20px;
    margin-right: 20px;
    width: 50%;

    > div {

      margin-top: 2em;

      &:first-child {
        margin-top: 0;
      }
    }

    > article {
      position: relative;

      :before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        z-index: -1;
        border-radius: 8px;
        background-color: rgba(black, 0.6);

        @media screen and (max-width: 1200px) {
          backdrop-filter: none;
        }
      }
    }


    @media screen and (max-width: 1200px) {
      float: none;
      margin: 0;
      width: 100%;
      box-sizing: border-box;
    }
  }

  .save-the-date {
    float: right;
    width: 40%;

    @media screen and (max-width: 1200px) {
      width: 100%;
      float: none;
    }
  }

  table {
    tr td {
      border-bottom: 1px solid rgba(white, 0.2);
      padding: 10px;
      vertical-align: top;

      &:first-child {
        padding-left: 0;
        white-space: nowrap;
      }

      &:last-child {
        padding-right: 0;
      }

      @media screen and (max-width: 1000px) {
        display: block;
        border-bottom: none;
      }
    }

    tr:last-child td {
      border-bottom: 0;
    }

    tr.upcoming {
      opacity: 0.5;
    }
  }
</style>
<i18n>
{
  "de": {
    "saveTheDate": "Die nächste Party findet am {nextPartyDay}. {nextPartyMonth} {nextPartyYear} statt - der Ticketverkauf startet am {ticketSaleStartDay}. {ticketSaleStartMonth} {ticketSaleStartYear} um {ticketSaleStartHour} Uhr!",
    "saveTheDateWithoutSale": "Die nächste Party findet am {nextPartyDay}. {nextPartyMonth} {nextPartyYear} statt - der Start des Ticketverkaufs wird rechtzeitig vorher bekanntgeben!",
    "underground_link": "UnderGround",
    "upcoming": {
      "title": "Termine und Fristen",
      "dates": [
        {
          "date": {
            "year": "2024",
            "month": "4",
            "day": "7"
          },
          "title": "Ticketverkauf beginnt um 18:00 Uhr",
          "description": "Verfügbarkeit solange der Vorrat reicht! Wir garantieren kein Angebot an der Abendkasse."
        },
        {
          "date": null,
          "title": "Ticketvorverkauf endet",
          "description": "Sobald alle Tickets verkauft sind, schließen wir den Verkauf. Ein Verkauf an der Abendkasse ist nicht geplant."
        }
      ]
    },
    "party_dates": [
      "Meet & Greet, Bondage, SM & Spank",
      "Play & Pamper",
      "Frechdax & Wiesn"
    ],
    "dateFormatWithWeekday": "{weekday}, {day}. {monthName} {year}",
    "dateFormat": "{day}. {monthName} {year}"
  },
  "en": {
    "saveTheDate": "The next party takes place on {nextPartyMonth} {nextPartyDay}, {nextPartyYear} - ticket sales start on {ticketSaleStartMonth} {ticketSaleStartDay}, {ticketSaleStartYear}, {ticketSaleStartHour}.00!",
    "saveTheDateWithoutSale": "The next party takes place on {nextPartyMonth} {nextPartyDay}, {nextPartyYear} - start of ticket sales will be announced as soon as possible.",
    "underground_link": "UnderGround",
    "upcoming": {
      "title": "Dates and Deadlines",
      "dates": [
        {
          "title": "Ticket sale starts at 6 pm (CEST)",
          "description": "Available while stocks last! We do not guarantee a ticket sale at the party evening."
        },
        {
          "title": "Ticket sale ends",
          "description": "As soon as we get sold out of our tickets, we will close the sale completely. It is not likely that you can get a ticket at the party evening."
        }
      ]
    },
    "party_dates": [
      "Meet & Greet, Bondage, SM & Spank",
      "Play & Pamper",
      "Frechdax & Wiesn"
    ],
    "dateFormatWithWeekday": "{weekday}, {monthName} {day}, {year}",
    "dateFormat": "{monthName} {day}, {year}"
  }
}
</i18n>
<i18n src="@/locales/global.json"/>
